import { AfterViewInit, Component, DestroyRef, ElementRef, inject, Input, OnChanges, OnDestroy, Renderer2, signal, SimpleChanges } from '@angular/core';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import get from 'lodash-es/get';

import { ContentNavigationService } from './content-navigation.service';
import { CommonModule } from '@angular/common';
import { AnchorComponent } from '../anchor/anchor.component';
import { PictureComponent } from '../picture/picture.component';
import { PolylineComponent } from '../polyline/polyline.component';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { ClickScrollToDirective } from '@finder/shared/directives/click-scroll-to/click-scroll-to.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'finder-content-navigation',
    templateUrl: './content-navigation.component.html',
    styleUrls: ['./content-navigation.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        AnchorComponent,
        PictureComponent,
        PolylineComponent,
        FontIconComponent,
        ClickScrollToDirective,
    ]
})
export class ContentNavigationComponent implements AfterViewInit, OnDestroy, OnChanges {

    @Input() links: any[];
    @Input() sidePolylines: any;
    @Input() logo: any;
    @Input() cta: any;
    @Input() ariaCurrent = '';
    @Input() blockClasses = null;
    @Input() ariaLabel;
    @Input() disableMobile = false;

    activeLink$$ = signal(void 0);

    offsetHeight = 0;

    private observer;
    private destroyRef = inject(DestroyRef);

    constructor(
        private contentNavigationService: ContentNavigationService,
        private elementRef: ElementRef,
        private windowRef: WindowRef,
        private renderer: Renderer2,
    ) { }

    get elementReference() {
        return this.elementRef.nativeElement;
    }

    ngAfterViewInit(): void {
        // Allow for disabling the content nav in mobile
        if (this.disableMobile !== true) {
            this.contentNavigationService.init(this.links, this.logo, this.cta, this.ariaLabel);
        } else {
            this.contentNavigationService.initActiveLink(this.links);
        }

        this.checkSidePolylines();
        this.observeSticky();

        this.contentNavigationService.observeActiveLinkChange()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (newLink) => {
                    this.activeLink$$.set(newLink);
                }
            });
    }

    ngOnDestroy() {
        this.contentNavigationService.disconnect();

        if (this.observer) {
            this.observer.disconnect();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.links.firstChange) {
            return;
        }

        this.contentNavigationService.update(
            changes.links.currentValue,
            changes.logo.currentValue,
            this.cta
        );
    }

    checkSidePolylines() {
        const polylines = this.sidePolylines;

        if (!polylines || !this.activeLink$$()) {
            return;
        }

        const activeLinkIndex = this.links.findIndex(link => link === this.activeLink$$());

        const previousLink = this.links[activeLinkIndex - 1];
        if (previousLink) {
            previousLink.polylineRight = get(polylines, 'polylineRight');
        }

        const nextLink = this.links[activeLinkIndex + 1];
        if (nextLink) {
            nextLink.polylineLeft = get(polylines, 'polylineLeft');
        }
    }

    private observeSticky() {
        // If there is no intersection observer then just add the `pinned` styling
        if (!this.windowRef.nativeWindow.IntersectionObserver) {
            this.renderer.addClass(this.elementReference, 'pinned');

            return;
        }

        this.observer = new this.windowRef.nativeWindow.IntersectionObserver(
            ([e]) => {
                e.target.classList.toggle('pinned', e.intersectionRatio < 1 && e.intersectionRect.top === 0);
            },
            { threshold: [1] }
        );

        this.observer.observe(this.elementReference);
    }
}
